import * as d3 from 'd3';

/**
 * Heatmap layer for visualizing location density
 */
export class HeatmapLayer {
  constructor(mapRenderer, layerId, color) {
    this.map = mapRenderer;
    this.layerId = layerId;
    this.color = color;
    this.layer = mapRenderer.getHeatmapLayer(layerId);
    this.radius = 20;  // Base radius for heatmap points
    this.clickCallback = null;  // Callback for click events
  }

  /**
   * Register a callback for location clicks
   * @param {Function} callback - Called with (locationName, metadata)
   */
  onLocationClick(callback) {
    this.clickCallback = callback;
  }

  /**
   * Update heatmap with new location counts
   * @param {Object} locationCounts - { locationId: count, ... }
   */
  update(locationCounts) {
    // Convert location counts to array of points with coordinates
    const points = [];

    for (const [locationId, count] of Object.entries(locationCounts)) {
      const location = this.map.locations[locationId];

      if (!location) {
        console.warn(`Location not found: ${locationId}`);
        continue;
      }

      // Parse lat/lon as they might be strings from Nominatim
      const lat = parseFloat(location.lat);
      const lon = parseFloat(location.lon);

      // Skip invalid coordinates
      if (isNaN(lat) || isNaN(lon)) {
        console.warn(`Invalid coordinates for ${locationId}:`, location.lat, location.lon);
        continue;
      }

      const [x, y] = this.map.project(lat, lon);
      points.push({ x, y, count, location });
    }

    console.log(`Rendering ${points.length} heatmap points for ${this.layerId}`);

    // Create scale for point size based on counts
    const maxCount = Math.max(...points.map(p => p.count), 1);
    const radiusScale = d3.scaleSqrt()
      .domain([1, maxCount])
      .range([5, 30]);

    // Render points
    const circles = this.layer.selectAll('circle')
      .data(points, d => d.location.name)
      .join(
        enter => {
          const circle = enter.append('circle')
            .attr('class', 'heatmap-point')
            .attr('cx', d => d.x)
            .attr('cy', d => d.y)
            .attr('r', 0)
            .attr('fill', this.color)
            .attr('opacity', 0.6)
            .style('cursor', 'pointer')
            .style('pointer-events', 'auto')
            .on('click', (event, d) => {
              event.stopPropagation();
              if (this.clickCallback) {
                this.clickCallback(d.location.name, this.layerId);
              }
            });

          // Add title for tooltip
          circle.append('title')
            .text(d => `${d.location.name}\n${d.count} mention${d.count !== 1 ? 's' : ''}`);

          circle.transition()
            .duration(300)
            .attr('r', d => radiusScale(d.count));

          return circle;
        },
        update => {
          // Update click handler (in case callback changed)
          update.on('click', (event, d) => {
            event.stopPropagation();
            if (this.clickCallback) {
              this.clickCallback(d.location.name, this.layerId);
            }
          });

          // Update position and size
          update.transition()
            .duration(300)
            .attr('cx', d => d.x)
            .attr('cy', d => d.y)
            .attr('r', d => radiusScale(d.count));

          // Update tooltip text
          update.select('title')
            .text(d => `${d.location.name}\n${d.count} mention${d.count !== 1 ? 's' : ''}`);

          return update;
        },
        exit => exit.transition()
          .duration(300)
          .attr('r', 0)
          .remove()
      );
  }

  /**
   * Clear the heatmap layer
   */
  clear() {
    this.layer.selectAll('circle')
      .transition()
      .duration(300)
      .attr('r', 0)
      .remove();
  }

  /**
   * Set the radius of heatmap points
   */
  setRadius(radius) {
    this.radius = radius;
  }
}
